import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LockKey, ArrowLeft, SignOut, Plus, PencilSimple, Trash, X, Briefcase, Users, ArrowClockwise, FilePdf, NotePencil, FloppyDisk, DownloadSimple } from "@phosphor-icons/react";
import {
  adminLogin,
  adminGetJobs,
  adminCreateJob,
  adminUpdateJob,
  adminDeleteJob,
  adminGetApplications,
  adminUpdateApplicationStatus,
  adminUpdateApplicationNotes,
  downloadApplicationPdf,
} from "@/lib/api";
import { BUSINESS } from "@/lib/data";

const TOKEN_KEY = "amwap_admin_token";
const empty = { title: "", location: "", job_type: "Full-time", pay_range: "", description: "", active: true };
const STATUSES = ["New", "Reviewing", "Interviewed", "Hired", "Not a fit"];
const STATUS_STYLES = {
  New: "bg-blue-100 text-blue-700",
  Reviewing: "bg-amber-100 text-amber-700",
  Interviewed: "bg-purple-100 text-purple-700",
  Hired: "bg-green-100 text-green-700",
  "Not a fit": "bg-slate-200 text-slate-600",
};
const field =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-navy-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-600 focus:border-navy-600 transition";
const label = "block text-sm font-semibold text-navy-900 mb-1.5";

export default function AdminCareers() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [tab, setTab] = useState("jobs");
  const [statusFilter, setStatusFilter] = useState("All");
  const [notesOpen, setNotesOpen] = useState(null); // application id
  const [detailsOpen, setDetailsOpen] = useState(null); // application id
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [editing, setEditing] = useState(null); // null | {job} | 'new'
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setJobs([]);
    setApps([]);
  }, []);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const [j, a] = await Promise.all([adminGetJobs(t), adminGetApplications(t)]);
      setJobs(j);
      setApps(a);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      } else {
        toast.error("Could not load careers data.");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const t = await adminLogin(passcode);
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setPasscode("");
      toast.success("Welcome back.");
    } catch {
      toast.error("Incorrect passcode.");
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setForm(empty);
    setEditing("new");
  };
  const openEdit = (job) => {
    setForm({
      title: job.title,
      location: job.location,
      job_type: job.job_type,
      pay_range: job.pay_range || "",
      description: job.description,
      active: job.active,
    });
    setEditing(job);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") {
        await adminCreateJob(token, form);
        toast.success("Job posted.");
      } else {
        await adminUpdateJob(token, editing.id, form);
        toast.success("Job updated.");
      }
      setEditing(null);
      load(token);
    } catch {
      toast.error("Could not save job.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (job) => {
    try {
      await adminUpdateJob(token, job.id, { ...job, active: !job.active });
      load(token);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const remove = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? This cannot be undone.`)) return;
    try {
      await adminDeleteJob(token, job.id);
      toast.success("Job deleted.");
      load(token);
    } catch {
      toast.error("Could not delete job.");
    }
  };

  const changeStatus = async (app, status) => {
    setApps((prev) => prev.map((a) => (a.id === app.id ? { ...a, status } : a)));
    try {
      await adminUpdateApplicationStatus(token, app.id, status);
      toast.success(`Marked "${app.name}" as ${status}.`);
    } catch {
      toast.error("Could not update status.");
      load(token);
    }
  };

  const openNotes = (app) => {
    setNotesOpen(app.id);
    setNoteDraft(app.notes || "");
  };

  const saveNotes = async (app) => {
    setSavingNote(true);
    try {
      const updated = await adminUpdateApplicationNotes(token, app.id, noteDraft);
      setApps((prev) => prev.map((a) => (a.id === app.id ? { ...a, notes: updated.notes, notes_updated_at: updated.notes_updated_at } : a)));
      toast.success("Notes saved.");
      setNotesOpen(null);
    } catch {
      toast.error("Could not save notes.");
    } finally {
      setSavingNote(false);
    }
  };

  const fmtNoteDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  const downloadPdf = async (app) => {
    try {
      await downloadApplicationPdf(token, app.id, app.name);
    } catch {
      toast.error("Could not generate PDF.");
    }
  };

  // ---- Login gate ----
  if (!token) {
    return (
      <div className="min-h-screen bg-navy-900 grain flex items-center justify-center px-5" data-testid="careers-admin-login">
        <div className="relative z-10 w-full max-w-md">
          <div className="flex items-center gap-3 mb-8 justify-center">
            <span className="w-14 h-14 rounded-full bg-white grid place-items-center overflow-hidden">
              <img src={BUSINESS.logo} alt="AMWAP" className="w-full h-full object-contain p-0.5" />
            </span>
          </div>
          <form onSubmit={handleLogin} className="bg-white rounded-3xl p-8 shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-navy-900 text-white grid place-items-center mb-5">
              <LockKey size={22} weight="fill" />
            </div>
            <h1 className="font-display font-black text-2xl text-navy-900 tracking-tight">Careers Admin</h1>
            <p className="text-slate-500 mt-2 text-sm">Enter your passcode to manage job postings.</p>
            <input
              type="password"
              data-testid="careers-admin-passcode"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              autoFocus
              className="w-full mt-6 rounded-xl border border-slate-300 px-4 py-3.5 text-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-600"
            />
            <button
              type="submit"
              disabled={loading || !passcode}
              data-testid="careers-admin-login-submit"
              className="w-full mt-4 rounded-full bg-navy-900 text-white py-3.5 font-bold hover:bg-navy-600 transition-colors disabled:opacity-60"
            >
              {loading ? "Checking…" : "Unlock"}
            </button>
            <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-navy-900">
              <ArrowLeft size={16} /> Back to website
            </Link>
          </form>
        </div>
      </div>
    );
  }

  // ---- Dashboard ----
  return (
    <div className="min-h-screen bg-slate-50" data-testid="careers-admin-dashboard">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1100px] mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-white ring-1 ring-slate-200 grid place-items-center overflow-hidden">
              <img src={BUSINESS.logo} alt="AMWAP" className="w-full h-full object-contain p-0.5" />
            </span>
            <span className="font-display font-extrabold text-navy-900">Careers Manager</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load(token)} data-testid="careers-refresh" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-navy-900 hover:bg-slate-100">
              <ArrowClockwise size={16} /> Refresh
            </button>
            <button onClick={logout} data-testid="careers-admin-logout" className="inline-flex items-center gap-2 rounded-full bg-navy-900 text-white px-4 py-2 text-sm font-bold hover:bg-navy-600">
              <SignOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-5 md:px-8 py-10">
        {/* Tabs */}
        <div className="flex items-center gap-2 mb-8">
          <button
            onClick={() => setTab("jobs")}
            data-testid="tab-jobs"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${tab === "jobs" ? "bg-navy-900 text-white" : "bg-white border border-slate-300 text-navy-900 hover:bg-slate-100"}`}
          >
            <Briefcase size={16} weight="fill" /> Jobs ({jobs.length})
          </button>
          <button
            onClick={() => setTab("apps")}
            data-testid="tab-apps"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${tab === "apps" ? "bg-navy-900 text-white" : "bg-white border border-slate-300 text-navy-900 hover:bg-slate-100"}`}
          >
            <Users size={16} weight="fill" /> Applications ({apps.length})
          </button>
        </div>

        {loading && <p className="text-slate-500">Loading…</p>}

        {/* Jobs tab */}
        {tab === "jobs" && !loading && (
          <>
            <div className="flex justify-end mb-5">
              <button onClick={openNew} data-testid="new-job-btn" className="inline-flex items-center gap-2 rounded-full bg-navy-900 text-white px-5 py-3 font-bold hover:bg-navy-600 transition-colors">
                <Plus size={18} weight="bold" /> New job
              </button>
            </div>
            {jobs.length === 0 ? (
              <p className="text-slate-500" data-testid="admin-no-jobs">No jobs yet. Click "New job" to post your first opening.</p>
            ) : (
              <div className="space-y-3">
                {jobs.map((job, i) => (
                  <div key={job.id} data-testid={`admin-job-${i}`} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-display font-bold text-lg text-navy-900 truncate">{job.title}</h3>
                        <span className={`shrink-0 text-xs font-bold px-2.5 py-0.5 rounded-full ${job.active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"}`}>
                          {job.active ? "Active" : "Hidden"}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1 truncate">{job.location} · {job.job_type}{job.pay_range ? ` · ${job.pay_range}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => toggleActive(job)} data-testid={`toggle-job-${i}`} className="text-xs font-bold rounded-full border border-slate-300 px-3 py-2 text-navy-900 hover:bg-slate-100">
                        {job.active ? "Hide" : "Publish"}
                      </button>
                      <button onClick={() => openEdit(job)} data-testid={`edit-job-${i}`} aria-label="Edit" className="w-9 h-9 grid place-items-center rounded-full border border-slate-300 text-navy-900 hover:bg-slate-100">
                        <PencilSimple size={16} />
                      </button>
                      <button onClick={() => remove(job)} data-testid={`delete-job-${i}`} aria-label="Delete" className="w-9 h-9 grid place-items-center rounded-full border border-red-300 text-red-600 hover:bg-red-500 hover:text-white transition-colors">
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Applications tab */}
        {tab === "apps" && !loading && (
          apps.length === 0 ? (
            <p className="text-slate-500" data-testid="admin-no-apps">No applications yet.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="status-filter-tabs">
                {["All", ...STATUSES].map((s) => {
                  const count = s === "All" ? apps.length : apps.filter((a) => (a.status || "New") === s).length;
                  const active = statusFilter === s;
                  return (
                    <button
                      key={s}
                      onClick={() => setStatusFilter(s)}
                      data-testid={`status-filter-${s.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors ${active ? "bg-navy-900 text-white" : "bg-white border border-slate-300 text-navy-900 hover:bg-slate-100"}`}
                    >
                      {s} <span className={`px-1.5 rounded-full ${active ? "bg-white/20" : "bg-slate-100"}`}>{count}</span>
                    </button>
                  );
                })}
              </div>
              {(() => {
                const filtered = statusFilter === "All" ? apps : apps.filter((a) => (a.status || "New") === statusFilter);
                if (filtered.length === 0) {
                  return <p className="text-slate-500" data-testid="admin-no-filtered-apps">No {statusFilter} applicants.</p>;
                }
                return (
                  <div className="space-y-3">
                    {filtered.map((a, i) => (
                      <div key={a.id} data-testid={`admin-app-${i}`} className="bg-white border border-slate-200 rounded-2xl p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-display font-bold text-lg text-navy-900">{a.name}</h3>
                            <p className="text-sm text-navy-600 font-semibold mt-0.5">{a.job_title}</p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <div className="flex items-center gap-3">
                              <button onClick={() => downloadPdf(a)} data-testid={`app-pdf-${i}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-navy-600 hover:text-navy-900">
                                <DownloadSimple size={15} weight="bold" /> PDF
                              </button>
                              <span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span data-testid={`app-status-badge-${i}`} className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_STYLES[a.status] || STATUS_STYLES.New}`}>
                                {a.status || "New"}
                              </span>
                              <select
                                data-testid={`app-status-select-${i}`}
                                value={a.status || "New"}
                                onChange={(e) => changeStatus(a, e.target.value)}
                                className="text-xs font-semibold rounded-full border border-slate-300 bg-white px-3 py-1.5 text-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-600"
                              >
                                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-slate-600">
                          <a href={`mailto:${a.email}`} className="text-navy-600 underline underline-offset-2">{a.email}</a>
                          <a href={`tel:${a.phone}`} className="text-navy-600 underline underline-offset-2">{a.phone}</a>
                          {a.resume && (
                            <a href={a.resume.url} target="_blank" rel="noreferrer" data-testid={`app-resume-${i}`} className="inline-flex items-center gap-1.5 font-semibold text-navy-900 underline underline-offset-2">
                              <FilePdf size={16} weight="fill" className="text-navy-600" /> {a.resume.filename}
                            </a>
                          )}
                        </div>
                        {a.message && <p className="text-slate-600 mt-3 text-sm whitespace-pre-wrap">{a.message}</p>}

                        {a.details && (
                          <div className="mt-3">
                            <button onClick={() => setDetailsOpen(detailsOpen === a.id ? null : a.id)} data-testid={`app-details-toggle-${i}`} className="text-xs font-bold text-navy-600 hover:text-navy-900 underline underline-offset-2">
                              {detailsOpen === a.id ? "Hide full application" : "View full application"}
                            </button>
                            {detailsOpen === a.id && (
                              <div data-testid={`app-details-${i}`} className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700 space-y-3">
                                <p><span className="font-semibold text-navy-900">Address:</span> {[a.details.address, a.details.city, a.details.state, a.details.zip].filter(Boolean).join(", ") || "-"}</p>
                                <p><span className="font-semibold text-navy-900">Desired pay:</span> {a.details.desired_pay || "-"} · <span className="font-semibold text-navy-900">Start:</span> {a.details.start_date || "-"} · <span className="font-semibold text-navy-900">Availability:</span> {a.details.availability || "-"}</p>
                                <p><span className="font-semibold text-navy-900">Work authorized:</span> {a.details.work_authorized || "-"} · <span className="font-semibold text-navy-900">Sponsorship:</span> {a.details.need_sponsorship || "-"} · <span className="font-semibold text-navy-900">License:</span> {a.details.drivers_license ? `${a.details.drivers_license}${a.details.dl_state ? ` (${a.details.dl_state})` : ""}` : "-"}</p>
                                {a.details.employment?.length > 0 && (
                                  <div><p className="font-semibold text-navy-900">Employment history</p><ul className="list-disc pl-5 mt-1 space-y-0.5">{a.details.employment.map((e, k) => <li key={k}>{e.title || "?"} @ {e.employer || "?"} ({e.start || "?"}–{e.end || "present"}){e.reason ? ` — ${e.reason}` : ""}</li>)}</ul></div>
                                )}
                                {a.details.education?.length > 0 && (
                                  <div><p className="font-semibold text-navy-900">Education</p><ul className="list-disc pl-5 mt-1 space-y-0.5">{a.details.education.map((e, k) => <li key={k}>{e.field || ""} — {e.school || ""} {e.year ? `(${e.year})` : ""}</li>)}</ul></div>
                                )}
                                {a.details.references?.length > 0 && (
                                  <div><p className="font-semibold text-navy-900">References</p><ul className="list-disc pl-5 mt-1 space-y-0.5">{a.details.references.map((r, k) => <li key={k}>{r.name || ""} {r.relationship ? `(${r.relationship})` : ""} — {r.contact || ""}</li>)}</ul></div>
                                )}
                                <p className="text-xs text-slate-500 pt-1 border-t border-slate-200">Signed by <span className="font-semibold">{a.details.signature || "-"}</span> · {a.details.signed_date || "-"} · Consent: {a.details.consent ? "Yes" : "No"}</p>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="mt-4 pt-4 border-t border-slate-100">
                          {notesOpen === a.id ? (
                            <div data-testid={`notes-editor-${i}`}>
                              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Private notes</label>
                              <textarea
                                rows={3}
                                autoFocus
                                data-testid={`notes-input-${i}`}
                                value={noteDraft}
                                onChange={(e) => setNoteDraft(e.target.value)}
                                placeholder="Interview thoughts, follow-ups, references… (only you can see this)"
                                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-navy-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-600"
                              />
                              <div className="flex items-center gap-2 mt-2">
                                <button onClick={() => saveNotes(a)} disabled={savingNote} data-testid={`notes-save-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-navy-900 text-white px-4 py-2 text-xs font-bold hover:bg-navy-600 transition-colors disabled:opacity-60">
                                  <FloppyDisk size={15} weight="fill" /> {savingNote ? "Saving…" : "Save notes"}
                                </button>
                                <button onClick={() => setNotesOpen(null)} data-testid={`notes-cancel-${i}`} className="rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-navy-900 hover:bg-slate-100">
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : a.notes ? (
                            <div data-testid={`notes-display-${i}`}>
                              <div className="flex items-center justify-between gap-3">
                                <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Private notes</span>
                                <button onClick={() => openNotes(a)} data-testid={`notes-edit-${i}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-navy-600 hover:text-navy-900">
                                  <NotePencil size={15} weight="fill" /> Edit
                                </button>
                              </div>
                              <p className="text-sm text-slate-700 mt-1.5 whitespace-pre-wrap bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">{a.notes}</p>
                              {a.notes_updated_at && (
                                <p className="text-[11px] text-slate-400 mt-1.5" data-testid={`notes-timestamp-${i}`}>Last updated {fmtNoteDate(a.notes_updated_at)}</p>
                              )}
                            </div>
                          ) : (
                            <button onClick={() => openNotes(a)} data-testid={`notes-add-${i}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-navy-600 hover:text-navy-900">
                              <NotePencil size={15} weight="fill" /> Add private notes
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </>
          )
        )}
      </main>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-[70] bg-navy-900/60 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={() => setEditing(null)} data-testid="job-editor">
          <div className="bg-white rounded-3xl w-full max-w-lg p-6 md:p-8 relative max-h-[90vh] overflow-y-auto" onMouseDown={(e) => e.stopPropagation()}>
            <button onClick={() => setEditing(null)} data-testid="job-editor-close" aria-label="Close" className="absolute top-5 right-5 w-9 h-9 grid place-items-center rounded-full border border-slate-300 text-navy-900 hover:bg-slate-100">
              <X size={18} />
            </button>
            <h3 className="font-display font-black text-2xl text-navy-900 tracking-tight">{editing === "new" ? "New job" : "Edit job"}</h3>
            <form onSubmit={save} className="mt-5 space-y-4">
              <div>
                <label className={label}>Title</label>
                <input required data-testid="job-title" className={field} placeholder="Handyman / Technician"
                  value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>Location</label>
                  <input required data-testid="job-location" className={field} placeholder="Charleston, SC"
                    value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                </div>
                <div>
                  <label className={label}>Job type</label>
                  <select data-testid="job-type" className={field} value={form.job_type} onChange={(e) => setForm({ ...form, job_type: e.target.value })}>
                    <option>Full-time</option>
                    <option>Part-time</option>
                    <option>Contract</option>
                    <option>Seasonal</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={label}>Pay range (optional)</label>
                <input data-testid="job-pay" className={field} placeholder="$20–$28 / hr"
                  value={form.pay_range} onChange={(e) => setForm({ ...form, pay_range: e.target.value })} />
              </div>
              <div>
                <label className={label}>Description & details</label>
                <textarea required rows={7} data-testid="job-description-input" className={field}
                  placeholder="Describe the role, responsibilities, requirements, and how to apply…"
                  value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <label className="flex items-center gap-3 text-sm font-semibold text-navy-900">
                <input type="checkbox" data-testid="job-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-5 h-5 rounded border-slate-300 text-navy-900 focus:ring-navy-600" />
                Active (visible on the public careers page)
              </label>
              <button type="submit" disabled={saving} data-testid="job-save" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 text-white py-3.5 font-bold hover:bg-navy-600 transition-colors disabled:opacity-60">
                {saving ? "Saving…" : editing === "new" ? "Post job" : "Save changes"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
